import type { FormEvent, ReactNode } from 'react'
import type {
    CustomPrice,
    PaymentMethod,
    PaymentMethods,
    SiteSettings
} from '@/types'
import type { CustomerForm } from '@/components/shop/customer-fields'
import type { DictionaryKey } from '@/lib/i18n'

import { useState } from 'react'
import { useRouter } from 'next/router'
import { Check } from 'lucide-react'
import { useT } from '@/lib/i18n'
import { cn } from '@/lib/utils'
import { buttonClass, cardClass, inputClass } from '@/lib/ui'
import { CustomerFields, emptyCustomer } from '@/components/shop/customer-fields'
import { Price, usePrice } from '@/components/shop/price'
import { PaymentMethodPicker, resolveMethod } from '@/components/shop/payment-method-picker'
import { FINISHES, HOME_COUNTRY, SIZES } from '@/constants/shop'

interface CustomOrderFormProps {
    prices: Array<CustomPrice>
    settings: SiteSettings | null
    methods?: PaymentMethods
}

const optionClass = (active: boolean) => cn(
    'flex w-full flex-col items-start rounded-2xl border-2 border-line p-4 text-left transition-transform hover:-translate-y-0.5',
    active ? 'bg-sun shadow-pop' : 'bg-card'
)

const NO_METHODS: PaymentMethods = { card: false, eft: false }

export const CustomOrderForm = ({ prices, settings, methods: given }: CustomOrderFormProps): ReactNode => {
    const methods = given ?? NO_METHODS
    const t = useT()
    const router = useRouter()
    const formatPrice = usePrice()
    const [size, setSize] = useState('A4')
    const [finish, setFinish] = useState('inked')
    const [description, setDescription] = useState('')
    const [customer, setCustomer] = useState<CustomerForm>(emptyCustomer)
    const [status, setStatus] = useState<'idle' | 'sending' | 'error'>('idle')
    const [message, setMessage] = useState('')
    const [errorField, setErrorField] = useState<string | undefined>()
    const [chosen, setChosen] = useState<PaymentMethod>('card')

    const method = resolveMethod(methods, chosen)
    const ready = methods.card || methods.eft

    const priceFor = (s: string, f: string) => prices.find(price => price.size === s && price.finish === f)
    const selected = priceFor(size, finish)
    const abroad = customer.country !== HOME_COUNTRY
    const shipping = abroad ? settings?.customShippingInternational ?? null : settings?.customShipping ?? 0
    const total = selected && shipping !== null ? selected.price + shipping : null

    const submit = async (event: FormEvent) => {
        event.preventDefault()
        setStatus('sending')
        setMessage('')
        setErrorField(undefined)

        try {
            const response = await fetch('/api/custom-order', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ size, finish, description, customer, method, locale: router.locale })
            })

            const data = await response.json()

            if (response.ok && data.url) {
                window.location.href = data.url

                return
            }

            setErrorField(data.field)
            setMessage(data.error === 'not-ready' ? t('checkoutNotReady') : data.error === 'invalid-email' ? t('invalidEmail') : data.error === 'required' ? t('required') : t('checkoutError'))
            setStatus('error')
        }
        catch {
            setMessage(t('checkoutError'))
            setStatus('error')
        }
    }

    return (
        <form onSubmit={submit} className='grid gap-8 lg:grid-cols-[1.4fr_1fr]'>
            <div className='space-y-8'>
                <fieldset>
                    <legend className='mb-3 font-heading text-2xl'>{t('chooseSize')}</legend>

                    <div className='grid gap-3 sm:grid-cols-2'>
                        {SIZES.map(option => (
                            <button key={option} type='button' onClick={() => setSize(option)} aria-pressed={size === option} className={optionClass(size === option)}>
                                <span className='flex w-full items-center justify-between text-xl font-bold'>
                                    {option}
                                    {size === option && <Check className='size-5' />}
                                </span>
                                <span className='text-sm text-muted-foreground'>{t(`size_${option}` as DictionaryKey)}</span>
                            </button>
                        ))}
                    </div>
                </fieldset>

                <fieldset>
                    <legend className='mb-3 font-heading text-2xl'>{t('chooseFinish')}</legend>

                    <div className='grid gap-3 sm:grid-cols-2'>
                        {FINISHES.map(option => {
                            const price = priceFor(size, option)

                            return (
                                <button
                                    key={option}
                                    type='button'
                                    disabled={!price}
                                    onClick={() => setFinish(option)}
                                    aria-pressed={finish === option}
                                    className={cn(optionClass(finish === option), !price && 'opacity-50')}
                                >
                                    <span className='flex w-full items-center justify-between font-bold'>
                                        {t(`finish_${option}` as DictionaryKey)}
                                        <span className='text-sm'>{price ? formatPrice(price.price) : t('unavailable')}</span>
                                    </span>
                                    <span className='mt-1 text-sm text-muted-foreground'>{t(`finishDesc_${option}` as DictionaryKey)}</span>
                                </button>
                            )
                        })}
                    </div>
                </fieldset>

                <div>
                    <label htmlFor='custom-description' className='mb-1.5 block font-heading text-2xl'>{t('describe')}</label>
                    <p id='custom-description-hint' className='mb-3 text-sm text-muted-foreground'>{t('describeHint')}</p>

                    <textarea
                        id='custom-description'
                        required
                        minLength={5}
                        maxLength={1500}
                        rows={5}
                        value={description}
                        onChange={event => setDescription(event.target.value)}
                        aria-describedby='custom-description-hint'
                        aria-invalid={errorField === 'description' || undefined}
                        className={inputClass}
                    />
                </div>

                <div>
                    <h3 className='mb-3 text-2xl'>{t('yourDetails')}</h3>
                    <CustomerFields value={customer} onChange={setCustomer} errorField={errorField} idPrefix='custom' />
                </div>
            </div>

            <aside className={cn(cardClass, 'h-fit space-y-4 bg-sky p-6 lg:sticky lg:top-24')}>
                <h3 className='text-2xl'>{t('orderSummary')}</h3>

                <dl className='space-y-2'>
                    <div className='flex justify-between gap-4'>
                        <dt>{size}, {t(`finish_${finish}` as DictionaryKey)}</dt>
                        <dd className='font-bold'>{selected ? <Price zar={selected.price} /> : t('unavailable')}</dd>
                    </div>

                    <div className='flex justify-between gap-4'>
                        <dt>{abroad ? t('shippingIntl') : t('shippingSA')}</dt>
                        <dd className='font-bold'>{shipping === null ? t('unavailable') : shipping === 0 ? t('freeShipping') : <Price zar={shipping} />}</dd>
                    </div>

                    <div className='flex justify-between gap-4 border-t-2 border-line pt-3 text-xl font-bold'>
                        <dt>{t('total')}</dt>
                        <dd>{total === null ? '-' : <Price zar={total} />}</dd>
                    </div>
                </dl>

                <p className='text-xs'>{t('payInRand')}</p>

                <PaymentMethodPicker methods={methods} value={method} onChange={setChosen} name='custom-payment' />

                {!ready && <p role='status' className='rounded-xl border-2 border-line bg-sun p-3 text-sm font-semibold'>{t('checkoutNotReady')}</p>}

                {message && <p role='alert' className='rounded-xl border-2 border-line bg-card p-3 text-sm font-semibold'>{message}</p>}

                <button type='submit' disabled={status === 'sending' || total === null || !ready} className={buttonClass('pink', 'w-full')}>
                    {status === 'sending'
                        ? method === 'eft' ? t('placingOrder') : t('redirecting')
                        : t(method === 'eft' ? 'placeOrder' : 'payNow', { amount: total === null ? '' : formatPrice(total) })}
                </button>
            </aside>
        </form>
    )
}
